/**
 * **What a section looks like**, drawn small — the picture on every card in the
 * picker (T-4.33: *"a picker that shows what each section looks like rather
 * than naming it"*).
 *
 * ## It is read off the schema, not written out per section
 *
 * The obvious build is twenty-two little drawings. This is not that, and the
 * reason is `lib/api/site.ts`'s rule 2: a second description of the sections
 * living in this bundle is a description that disagrees with the service the
 * day one of them changes. Twenty-two drawings would have been the largest
 * such copy in the product.
 *
 * So `sectionShape` **reads the schema the service published** and turns it
 * into a handful of primitives — a heading is a bar, a body is two lines, an
 * image is a box, a repeating group is a row of tiles whose inside comes from
 * that group's own sub-fields, an arrangement `enum` decides whether the tiles
 * sit in a grid or a band and which side the picture goes. A twenty-third
 * section gets a picture on the day the service serves it, with nothing written
 * here.
 *
 * ## The one thing it cannot read, and why it is a table of two
 *
 * `ARRANGEMENT` overrides the derivation for what the schema genuinely does not
 * say. Both entries are facts about the **renderer**, cited where they are
 * written down, and neither is a field, a label or a limit.
 *
 * ## What it deliberately is not
 *
 * Not a preview of the firm's own content, and not a screenshot of its
 * template. A firm adding its fourth section is choosing a *shape*; showing it
 * eight thumbnails of its own half-written home page would be showing it eight
 * pictures of the same thing. `/_demo/<code>` is where a template is seen whole
 * (FR-I10), and the card says which template the firm is on.
 */

import type { BlockSchema, BlockType, FieldSchema } from '../../lib/api/site'

// -- the primitives ----------------------------------------------------------

/** How a repeating group's tiles sit. */
export type Arrangement =
  | 'grid'
  | 'band'
  | 'rows'
  | 'chips'
  | 'numbered'
  // C21 (ADR-0043 §2) — facts about `site/blocks/library-two.tsx`, below.
  | 'tabs'
  | 'timeline'
  | 'ticker'
  | 'table'
  | 'leaders'
  | 'week'

/** What one tile of a repeating group looks like inside. */
export type Tile =
  | 'card'
  | 'link-card'
  | 'figure'
  | 'portrait'
  | 'quote'
  | 'chip'
  | 'qa'
  // C21 — a plan with its price, a picture card, a product, a pair of pictures.
  | 'plan'
  | 'picture'
  | 'product'
  | 'pair'

export type Shape =
  | { kind: 'eyebrow' }
  | { kind: 'heading'; big: boolean }
  | { kind: 'sub' }
  | { kind: 'lines' }
  | { kind: 'media' }
  | { kind: 'actions'; count: number }
  | { kind: 'repeat'; tile: Tile; arrangement: Arrangement; live: boolean }
  | { kind: 'form' }
  /** C21 — a poster with a play mark: a picture beside a required address. */
  | { kind: 'video' }
  /** C21 — a bar filled from two numbers the firm typed (`goal`). */
  | { kind: 'meter' }

export interface SectionShapeSpec {
  /** Drawn side by side, the picture on this side — a `left`/`right` enum. */
  split: 'left' | 'right' | null
  /** A `left`/`centre` enum resolved to its default. */
  centred: boolean
  rows: Shape[]
}

/**
 * The two the schema cannot say, both facts about `site/blocks/library.tsx`.
 *
 * - `steps` renders an `<ol>` and *"the number is the position"* (FR-I14, and
 *   `library.tsx: Steps`). The numerals are most of what the section looks
 *   like, and no field carries them precisely because they are never typed.
 * - `logo_strip` is names set as type in one running band, never a grid of
 *   cards — the section carries no image at all and v1 says so (FR-I14, §9-43).
 *   Its single sub-field would derive to a chip anyway; this fixes the band.
 */
const ARRANGEMENT: Partial<Record<BlockType, Arrangement>> = {
  steps: 'numbered',
  logo_strip: 'chips',
  // C17 (ADR-0036 §2), facts about `site/blocks/events.tsx`: a schedule's
  // days sit side by side as tabs across the section, and tickets are passes
  // side by side in one band. Without these both derive to a grid of cards,
  // and tickets to exactly `feature_grid`'s picture (as `rows`, a schedule is
  // exactly `story_strip`'s). The tiles still differ — a day is a card, a pass
  // a link-card. Wave 18B's minimum; 18D owns the picker and may draw better.
  schedule: 'band',
  tickets: 'band',
  // C21 (Wave 21G, T-4.90, ADR-0043 §2) — facts about
  // `site/blocks/library-two.tsx`, none of which a field says: tabs are a row
  // of labels over one panel; a timeline is a rail with a point per entry; the
  // moving band is words in one running strip, not `logo_strip`'s chips; a
  // comparison is a table (its two lists, columns and rows, are one drawing);
  // a price list is names and prices joined by a dotted leader; a role is a
  // row in a list, not a card.
  tabs: 'tabs',
  timeline: 'timeline',
  marquee: 'ticker',
  comparison: 'table',
  price_list: 'leaders',
  openings: 'rows',
}

/**
 * C21 — what a tile of the new sections looks like, where `tileFor` would
 * derive a shape another section already has. Facts about
 * `site/blocks/library-two.tsx`: a pricing card leads with a price and one of
 * them is ringed; a course is a picture card, not `team`'s round portrait; a
 * product is a square picture with its price; a before-and-after is two
 * pictures meeting at a grip.
 */
const TILE: Partial<Record<BlockType, Tile>> = {
  pricing: 'plan',
  courses: 'picture',
  product_grid: 'product',
  before_after: 'pair',
}

/**
 * C21 — a section whose content is **read at render** and has no field to
 * derive a picture from, as `lead_form`'s form. `hours` draws *Your firm*'s
 * week, today marked (ADR-0043 §2).
 */
const READ_AT_RENDER: Partial<Record<BlockType, Arrangement>> = {
  hours: 'week',
}

// -- reading a schema --------------------------------------------------------

const HEADING_NAMES = new Set(['heading', 'headline'])

function tileFor(of: readonly FieldSchema[], type?: BlockType): Tile {
  const override = type ? TILE[type] : undefined
  if (override) return override
  if (of.some((f) => f.kind === 'image')) return 'portrait'
  if (of.some((f) => f.name === 'quote')) return 'quote'
  if (of.some((f) => f.name === 'question')) return 'qa'
  // A short required string beside a label is a figure — `metrics` caps its
  // `value` at 24 characters precisely because it is one.
  if (of.length >= 2 && of.some((f) => f.required && (f.max_length ?? 999) <= 24)) return 'figure'
  if (of.length === 1) return 'chip'
  // C20: a tier's link is now its own buttons (ADR-0041 §1) — still a card
  // that leads somewhere.
  if (of.some((f) => f.kind === 'url' || f.kind === 'buttons')) return 'link-card'
  return 'card'
}

function arrangementFor(
  schema: BlockSchema,
  field: FieldSchema,
  enums: FieldSchema[],
  tile: Tile,
): Arrangement {
  const override = ARRANGEMENT[schema.type]
  if (override) return override
  // An arrangement enum names its own answer: `grid`|`list`, `cards`|`band`,
  // `alternating`|`stacked`. The default is what the firm gets until it
  // chooses, so the default is what the picture shows.
  const layout = enums.find((f) => f.name === 'layout')
  const chosen = typeof layout?.default === 'string' ? layout.default : null
  if (chosen === 'list' || chosen === 'stacked' || chosen === 'alternating') return 'rows'
  if (chosen === 'band') return 'band'
  if (tile === 'chip') return 'chips'
  if (tile === 'qa') return 'rows'
  // A quote does not fit across a third of the width, and the schema says so:
  // `testimonial_strip`'s quote runs to 600 characters and `story_strip`'s
  // story to 800, where a `feature_grid` item's body stops at 300.
  const longest = Math.max(0, ...(field.of ?? []).map((f) => f.max_length ?? 0))
  return longest > 400 ? 'rows' : 'grid'
}

/**
 * One section's shape, from the schema the service published.
 *
 * Read tolerantly: an unknown `kind`, a field the schema grew and this
 * derivation has no primitive for, contributes nothing rather than breaking the
 * card — the rule `BlockForm` keeps for the same reason.
 */
export function sectionShape(schema: BlockSchema): SectionShapeSpec {
  const fields = schema.fields
  const enums = fields.filter((f) => f.kind === 'enum')
  const rows: Shape[] = []

  const sideEnum = enums.find(
    (f) => (f.choices ?? []).includes('left') && (f.choices ?? []).includes('right'),
  )
  const alignEnum = enums.find(
    (f) => (f.choices ?? []).includes('left') && (f.choices ?? []).includes('centre'),
  )

  // A section whose only content is one line draws that line large — which is
  // what `statement` is, and the schema says so by carrying no body, no
  // introduction and no repeating group at all.
  const onlyALine = !fields.some(
    (f) => f.kind === 'long_text' || f.kind === 'rich' || f.kind === 'items' || f.kind === 'slugs',
  )

  // C21 — a picture beside a required address is a poster that links out
  // (`video`): drawn with a play mark, and its address is not a button.
  const posterLink = fields.some((f) => f.kind === 'image') && fields.find((f) => f.kind === 'url' && f.required)

  let actions = 0
  let drewTable = false
  for (const field of fields) {
    if (field.name === 'eyebrow') rows.push({ kind: 'eyebrow' })
    else if (posterLink && field.kind === 'image') rows.push({ kind: 'video' })
    else if (posterLink && field === posterLink) continue
    else if (field.kind === 'int' && field.name === 'target') rows.push({ kind: 'meter' })
    else if (HEADING_NAMES.has(field.name)) rows.push({ kind: 'heading', big: onlyALine })
    else if (field.name === 'subheading') rows.push({ kind: 'sub' })
    else if (field.kind === 'long_text' || field.kind === 'rich') rows.push({ kind: 'lines' })
    else if (field.kind === 'image') rows.push({ kind: 'media' })
    else if (field.kind === 'buttons') {
      // C20, ADR-0041 §1 — the firm's own buttons, up to `max_items` of them.
      actions += field.max_items ?? 2
    } else if (field.name.endsWith('_href') || field.name.endsWith('_label')) {
      // A label and its link are one button between them; count the links.
      if (field.name.endsWith('_href')) actions += 1
    } else if (field.kind === 'items') {
      const tile = tileFor(field.of ?? [], schema.type)
      const arrangement = arrangementFor(schema, field, enums, tile)
      // A comparison's columns and rows are one table, drawn once.
      if (arrangement === 'table') {
        if (drewTable) continue
        drewTable = true
      }
      rows.push({
        kind: 'repeat',
        tile,
        arrangement,
        live: false,
      })
    } else if (field.kind === 'slugs') {
      // A slug list is a reference, never content (FR-I14 rule 3): what it
      // draws is whatever the catalogue holds at render.
      const tile = TILE[schema.type] ?? 'card'
      rows.push({
        kind: 'repeat',
        tile,
        arrangement: arrangementFor(schema, field, enums, tile),
        live: true,
      })
    } else if (field.kind === 'int' && field.name === 'count') {
      rows.push({ kind: 'repeat', tile: 'card', arrangement: 'grid', live: true })
    }
  }

  // `contact` and `lead_form` put a form on the page — one from a `bool` the
  // firm can turn off, one by being a form. Neither has a field describing it,
  // so it is read from the two that do: a form's services, and its button.
  if (fields.some((f) => f.name === 'show_form') || schema.type === 'lead_form') {
    rows.push({ kind: 'form' })
  }
  const atRender = READ_AT_RENDER[schema.type]
  if (atRender) rows.push({ kind: 'repeat', tile: 'chip', arrangement: atRender, live: true })
  if (actions > 0) rows.push({ kind: 'actions', count: actions })

  // A picture and a list beside each other is a split; a picture with nothing
  // to sit beside is just a picture.
  const split = sideEnum && rows.some((r) => r.kind === 'media') ? sideEnum.default : null

  return {
    split: split === 'left' || split === 'right' ? split : null,
    centred: alignEnum?.default === 'centre',
    // A `lead_form` with one slug list would otherwise draw the catalogue's
    // cards; what it actually draws is a form with a dropdown in it.
    rows: schema.type === 'lead_form' ? rows.filter((r) => r.kind !== 'repeat') : rows,
  }
}

// -- drawing it --------------------------------------------------------------

const BAR = 'rounded-full bg-muted-foreground/35'
const FAINT = 'rounded-full bg-muted-foreground/20'
const BOX = 'rounded bg-muted-foreground/15'
const LIVE = 'rounded border border-dashed border-primary/50 bg-primary/10'

function Tiles({ tile, arrangement, live }: { tile: Tile; arrangement: Arrangement; live: boolean }) {
  const skin = live ? LIVE : BOX
  const count = arrangement === 'chips' ? 5 : arrangement === 'rows' ? 2 : 3

  if (arrangement === 'chips') {
    return (
      <div className="flex flex-wrap gap-1">
        {Array.from({ length: count }, (_, i) => (
          <span key={i} className={`${FAINT} h-1.5 w-5`} />
        ))}
      </div>
    )
  }

  // -- C21's arrangements (ADR-0043 §2) --

  if (arrangement === 'tabs') {
    return (
      <div className="space-y-1">
        <div className="flex gap-1">
          {Array.from({ length: 3 }, (_, i) => (
            <span key={i} className={`h-1.5 w-6 rounded-full ${i === 0 ? 'bg-primary/40' : 'bg-muted-foreground/20'}`} />
          ))}
        </div>
        <div className={`${skin} flex gap-1 p-1`}>
          <span className="block flex-1 space-y-0.5">
            <span className={`${BAR} block h-1 w-3/4`} />
            <span className={`${FAINT} block h-1 w-full`} />
          </span>
          {tile === 'portrait' && <span className="block h-4 w-6 rounded-sm bg-muted-foreground/30" />}
        </div>
      </div>
    )
  }

  if (arrangement === 'timeline') {
    return (
      <div className="relative space-y-1 border-l border-muted-foreground/40 pl-2">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="relative flex items-center gap-1">
            <span className="absolute -left-[11px] h-1.5 w-1.5 rounded-full bg-primary/50" />
            <span className={`${BAR} block h-1 w-4`} />
            <span className={`${FAINT} block h-1 flex-1`} />
          </div>
        ))}
      </div>
    )
  }

  if (arrangement === 'ticker') {
    return (
      <div className="flex items-center gap-1.5 overflow-hidden rounded-sm bg-muted-foreground/10 px-1 py-1.5">
        {Array.from({ length: 5 }, (_, i) => (
          <span key={i} className="flex shrink-0 items-center gap-1.5">
            <span className={`${BAR} block h-1.5 w-6`} />
            <span className="h-1 w-1 rounded-full bg-primary/40" />
          </span>
        ))}
      </div>
    )
  }

  if (arrangement === 'table') {
    return (
      <div className="grid grid-cols-4 gap-px overflow-hidden rounded-sm border border-muted-foreground/25">
        {Array.from({ length: 12 }, (_, i) => (
          <span
            key={i}
            className={`block h-2 ${i < 4 ? 'bg-muted-foreground/30' : i % 4 === 0 ? 'bg-muted-foreground/20' : 'bg-muted-foreground/10'}`}
          />
        ))}
      </div>
    )
  }

  if (arrangement === 'leaders') {
    return (
      <div className="space-y-1">
        <span className={`${BAR} block h-1 w-1/4`} />
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="flex items-end gap-1">
            <span className={`${BAR} block h-1 w-1/3`} />
            <span className="block flex-1 border-b border-dotted border-muted-foreground/50" />
            <span className={`${BAR} block h-1 w-3`} />
          </div>
        ))}
      </div>
    )
  }

  if (arrangement === 'week') {
    return (
      <div className={`${live ? LIVE : BOX} space-y-0.5 p-1`}>
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className={`flex justify-between gap-2 ${i === 1 ? 'rounded-sm bg-primary/25 px-0.5' : ''}`}>
            <span className={`${BAR} block h-1 w-4`} />
            <span className={`${FAINT} block h-1 w-8`} />
          </div>
        ))}
      </div>
    )
  }

  if (arrangement === 'numbered') {
    return (
      <div className="space-y-1">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="flex items-center gap-1">
            <span className="flex h-3 w-3 items-center justify-center rounded-full bg-primary/25 text-[5px] font-bold leading-none text-primary">
              {i + 1}
            </span>
            <span className={`${BAR} h-1 flex-1`} />
          </div>
        ))}
      </div>
    )
  }

  if (arrangement === 'rows') {
    return (
      <div className="space-y-1">
        {Array.from({ length: count }, (_, i) => (
          <div key={i} className={`${skin} space-y-0.5 p-1`}>
            <span className={`${BAR} block h-1 w-2/3`} />
            {tile !== 'qa' && <span className={`${FAINT} block h-1 w-full`} />}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={arrangement === 'band' ? 'flex gap-1' : 'grid grid-cols-3 gap-1'}>
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          className={`${skin} flex-1 space-y-0.5 p-1 ${tile === 'plan' && i === 1 ? 'ring-1 ring-primary/60' : ''}`}
        >
          {tile === 'portrait' && <span className="mx-auto block h-3 w-3 rounded-full bg-muted-foreground/30" />}
          {tile === 'picture' && <span className="block h-3 w-full rounded-sm bg-muted-foreground/30" />}
          {tile === 'product' && <span className="block aspect-square w-full rounded-sm bg-muted-foreground/30" />}
          {tile === 'pair' ? (
            <span className="flex h-5 overflow-hidden rounded-sm">
              <span className="block flex-1 bg-muted-foreground/35" />
              <span className="block w-px bg-primary" />
              <span className="block flex-1 bg-muted-foreground/15" />
            </span>
          ) : tile === 'plan' ? (
            <>
              <span className={`${FAINT} block h-1 w-1/2`} />
              <span className={`${BAR} block h-2 w-2/3`} />
              <span className="block h-1.5 w-full rounded-sm bg-primary/40" />
            </>
          ) : tile === 'figure' ? (
            <>
              <span className={`${BAR} mx-auto block h-2 w-1/2`} />
              <span className={`${FAINT} mx-auto block h-1 w-3/4`} />
            </>
          ) : (
            <>
              <span className={`${BAR} block h-1 w-3/4`} />
              <span className={`${FAINT} block h-1 w-full`} />
              {(tile === 'quote' || tile === 'link-card') && (
                <span className={`${FAINT} block h-1 w-1/2`} />
              )}
            </>
          )}
        </div>
      ))}
    </div>
  )
}

function Row({ shape }: { shape: Shape }) {
  switch (shape.kind) {
    case 'eyebrow':
      return <span className={`${FAINT} block h-1 w-1/4`} />
    case 'heading':
      return <span className={`${BAR} block ${shape.big ? 'h-2.5 w-full' : 'h-1.5 w-2/3'}`} />
    case 'sub':
      return <span className={`${FAINT} block h-1 w-1/2`} />
    case 'lines':
      return (
        <span className="block space-y-0.5">
          <span className={`${FAINT} block h-1 w-full`} />
          <span className={`${FAINT} block h-1 w-5/6`} />
        </span>
      )
    case 'media':
      return <span className={`${BOX} block h-6 w-full`} />
    case 'form':
      return (
        <span className="block space-y-0.5">
          <span className={`${BOX} block h-1.5 w-full`} />
          <span className={`${BOX} block h-1.5 w-full`} />
        </span>
      )
    case 'actions':
      return (
        <span className="flex gap-1">
          {Array.from({ length: Math.min(shape.count, 2) }, (_, i) => (
            <span key={i} className="h-2 w-7 rounded-sm bg-primary/40" />
          ))}
        </span>
      )
    case 'video':
      return (
        <span className={`${BOX} flex h-6 w-full items-center justify-center`}>
          <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-primary/40">
            <span className="ml-px block h-0 w-0 border-y-[3px] border-l-[5px] border-y-transparent border-l-primary-foreground" />
          </span>
        </span>
      )
    case 'meter':
      return (
        <span className="block space-y-0.5">
          <span className="flex justify-between">
            <span className={`${BAR} block h-1.5 w-1/4`} />
            <span className={`${FAINT} block h-1.5 w-1/5`} />
          </span>
          <span className="block h-1.5 w-full overflow-hidden rounded-full bg-muted-foreground/15">
            <span className="block h-full w-3/5 rounded-full bg-primary/50" />
          </span>
        </span>
      )
    case 'repeat':
      return <Tiles tile={shape.tile} arrangement={shape.arrangement} live={shape.live} />
  }
}

/**
 * The drawing. `aria-hidden`, and deliberately: every card that carries one
 * also carries the section's name and its description in text, and a screen
 * reader being read a wireframe bar by bar is worse than not being read it at
 * all (T-4.35).
 */
export function SectionShape({ schema }: { schema: BlockSchema }) {
  const spec = sectionShape(schema)
  const rest = spec.rows.filter((r) => r.kind !== 'media')

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none h-[72px] overflow-hidden rounded border border-border bg-surface-raised p-2"
    >
      {spec.split ? (
        <div className={`flex gap-2 ${spec.split === 'left' ? '' : 'flex-row-reverse'}`}>
          <div className="w-2/5 shrink-0">
            <span className={`${BOX} block h-12 w-full`} />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            {rest.map((shape, i) => (
              <Row key={i} shape={shape} />
            ))}
          </div>
        </div>
      ) : (
        <div className={`space-y-1 ${spec.centred ? 'text-center [&>span]:mx-auto' : ''}`}>
          {spec.rows.map((shape, i) => (
            <Row key={i} shape={shape} />
          ))}
        </div>
      )}
    </div>
  )
}
